import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import Modal, { Field, TextInput, Select, PrimaryButton, SecondaryButton, GhostButton } from "@/components/FormKit";
import { fmtCurrency } from "@/lib/format";
import { toast } from "sonner";

const initial = {
  name: "",
  price: 49,
  billing_cycle: "monthly",
  max_projects: 5,
  max_customers: 100,
  max_portal_users: 10,
  max_sites: 5,
  description: "",
};

export default function Plans() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/platform/plans");
    setItems(r.data);
  };
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/platform/plans", {
        ...form,
        price: Number(form.price),
        max_projects: Number(form.max_projects),
        max_customers: Number(form.max_customers),
        max_portal_users: Number(form.max_portal_users),
        max_sites: Number(form.max_sites),
      });
      toast.success("Plan created");
      setOpen(false);
      setForm(initial);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this plan?")) return;
    try {
      await api.delete(`/platform/plans/${id}`);
      toast.success("Plan deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-8" data-testid="platform-plans-page">
      <PageHeader
        eyebrow="Catalogue"
        title="Subscription plans"
        description="Control limits on projects, customers, portal users, and sites by tier."
        actions={
          <PrimaryButton onClick={() => setOpen(true)} testId="new-plan-button">
            + New Plan
          </PrimaryButton>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="plans-grid">
        {items.map((p) => (
          <div key={p.id} className="grid-surface p-6 flex flex-col" data-testid={`plan-${p.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="overline text-brand-600">{p.billing_cycle}</div>
                <div className="font-display text-2xl font-black mt-1">{p.name}</div>
              </div>
              <GhostButton onClick={() => remove(p.id)} testId={`delete-plan-${p.id}`}>
                Delete
              </GhostButton>
            </div>
            <div className="mt-3 font-display text-4xl font-black">
              {fmtCurrency(p.price)}
              <span className="text-sm font-normal text-stone-500">
                /{p.billing_cycle === "yearly" ? "yr" : "mo"}
              </span>
            </div>
            <p className="text-sm text-stone-600 mt-2 min-h-[40px]">{p.description}</p>
            <div className="mt-4 grid grid-cols-2 gap-2 text-xs font-mono border-t border-stone-200 pt-3">
              <div className="flex justify-between">
                <span className="text-stone-500">Projects</span> <b>{p.max_projects}</b>
              </div>
              <div className="flex justify-between">
                <span className="text-stone-500">Customers</span> <b>{p.max_customers}</b>
              </div>
              <div className="flex justify-between">
                <span className="text-stone-500">Users</span> <b>{p.max_portal_users}</b>
              </div>
              <div className="flex justify-between">
                <span className="text-stone-500">Sites</span> <b>{p.max_sites}</b>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="grid-surface border-dashed p-8 text-center col-span-full">
            <div className="overline text-stone-400">No plans yet</div>
          </div>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="New subscription plan">
        <form onSubmit={submit}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name">
              <TextInput testId="form-plan-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label="Price (INR)">
              <TextInput testId="form-plan-price" type="number" min="0" step="0.01" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </Field>
            <Field label="Billing cycle">
              <Select testId="form-plan-cycle" value={form.billing_cycle} onChange={(e) => setForm({ ...form, billing_cycle: e.target.value })}>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </Select>
            </Field>
            <Field label="Max projects">
              <TextInput testId="form-plan-max-projects" type="number" min="1" value={form.max_projects} onChange={(e) => setForm({ ...form, max_projects: e.target.value })} />
            </Field>
            <Field label="Max customers">
              <TextInput testId="form-plan-max-customers" type="number" min="1" value={form.max_customers} onChange={(e) => setForm({ ...form, max_customers: e.target.value })} />
            </Field>
            <Field label="Max portal users">
              <TextInput testId="form-plan-max-users" type="number" min="1" value={form.max_portal_users} onChange={(e) => setForm({ ...form, max_portal_users: e.target.value })} />
            </Field>
            <Field label="Max sites">
              <TextInput testId="form-plan-max-sites" type="number" min="1" value={form.max_sites} onChange={(e) => setForm({ ...form, max_sites: e.target.value })} />
            </Field>
          </div>
          <Field label="Description">
            <TextInput testId="form-plan-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <SecondaryButton type="button" onClick={() => setOpen(false)}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" disabled={busy} testId="submit-plan">{busy ? "Creating…" : "Create"}</PrimaryButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
